import { useEffect, useRef, useState } from 'react';

/**
 * useSameAsCopy
 *
 * Manages "same as X" copy semantics for form sections: copy a source's values into the
 * target, then drop the link as soon as either side is edited by hand.
 *
 * Change detection compares serialized snapshots held in refs rather than passing
 * `targetDeps` / `sourceDepsMap[activeSource]` straight to `useEffect`. Those arrays vary
 * in length between renders — `[]` while unlinked, one entry per watched field once a
 * source is active, and sources are not all the same width (in `StepPOC`, owner exposes
 * five fields against architect's six). React compares dependency lists positionally and
 * cannot handle one that changes size, so the unlink effect fired unpredictably when the
 * active source changed. Fixed-size dependencies also let `react-hooks/exhaustive-deps`
 * verify this hook, which it previously reported as unverifiable.
 */
export function useSameAsCopy({ targetDeps, sources, sourceDepsMap, onCopy }) {
  const [activeSource, setActiveSource] = useState(null);

  const targetSignature = JSON.stringify(targetDeps);
  const sourceSignature = JSON.stringify(activeSource ? sourceDepsMap[activeSource] : null);

  const prevTarget = useRef(targetSignature);
  const prevSource = useRef(sourceSignature);
  const prevActiveSource = useRef(activeSource);

  useEffect(() => {
    const targetEdited = prevTarget.current !== targetSignature;
    const sourceEdited = prevSource.current !== sourceSignature;
    const linkChanged = prevActiveSource.current !== activeSource;

    prevTarget.current = targetSignature;
    prevSource.current = sourceSignature;
    prevActiveSource.current = activeSource;

    // Linking or unlinking rewrites both snapshots by definition — that is the copy
    // itself, not a manual edit — so such a pass only re-baselines the comparison.
    // This replaces the previous `isCopyingRef` + `queueMicrotask` guard, which depended
    // on the microtask draining after React flushed passive effects rather than before.
    if (linkChanged) return;
    if (!activeSource) return;

    if (targetEdited || sourceEdited) {
      setActiveSource(null);
    }
  }, [targetSignature, sourceSignature, activeSource]);

  const copyFrom = (sourceKey) => {
    const source = sources[sourceKey];
    if (!source) return;

    setActiveSource(sourceKey);
    onCopy({ ...source });
  };

  const unlink = () => {
    setActiveSource(null);
  };

  return {
    activeSource,
    copyFrom,
    unlink,
  };
}
